import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import type { ComponentProps } from 'react';
import { ChevronDownIcon } from '../lib/icons.tsx';
import { cx, styled } from '../lib/styled.tsx';

/**
 * The trigger sets `data-panel-open` on itself, so the chevron can be flipped
 * from the row without a second source of truth for "is this item open".
 */
function AccordionTrigger({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseAccordion.Trigger>) {
  return (
    <BaseAccordion.Trigger
      className={cx(
        'group flex w-full items-center justify-between gap-4 py-3 text-left',
        'text-sm font-medium text-ink select-none outline-none',
        'hover:not-data-disabled:text-brand-600',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="shrink-0 text-ink-muted transition-transform duration-150 ease-out-quint group-data-panel-open:rotate-180" />
    </BaseAccordion.Trigger>
  );
}

/**
 * Padding lives on an inner element, not on the panel: the open/close animation
 * interpolates the panel's own height, and vertical padding on the animated box
 * would keep it from ever reaching zero.
 */
function AccordionPanel({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseAccordion.Panel>) {
  return (
    <BaseAccordion.Panel
      className={cx(
        'h-[var(--accordion-panel-height)] overflow-hidden',
        'transition-[height] duration-200 ease-out-quint',
        'data-starting-style:h-0 data-ending-style:h-0',
        className,
      )}
      {...props}
    >
      <div className="pb-4 text-sm text-ink-muted">{children}</div>
    </BaseAccordion.Panel>
  );
}

/** A stack of disclosure rows. Pass `multiple` on the root to open several at once. */
export const Accordion = {
  Root: styled(BaseAccordion.Root, 'flex w-full flex-col'),
  Item: styled(BaseAccordion.Item, 'border-b border-border-subtle last:border-b-0'),
  Header: styled(BaseAccordion.Header, 'flex'),
  Trigger: AccordionTrigger,
  Panel: AccordionPanel,
};
